import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { adminAssignQuizzes, adminCreateGroup, adminGetGroups, adminGetQuizzes } from '../../api/adminApi';
import { useAdminAuth } from '../../Context/AdminAuthContext';

export default function AdminGroups() {
    const { token } = useAdminAuth();
    const [groups, setGroups] = useState<any[]>([]);
    const [allQuizzes, setAllQuizzes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Create group dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newSlug, setNewSlug] = useState('');

    // Assign quizzes dialog
    const [assignGroup, setAssignGroup] = useState<any | null>(null);
    const [selectedQuizIds, setSelectedQuizIds] = useState<number[]>([]);

    const load = () => {
        if (!token) return;
        Promise.all([
            adminGetGroups(token),
            adminGetQuizzes(token).catch(() => [] as any[]),
        ])
            .then(([g, q]) => { setGroups(g); setAllQuizzes(q); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(load, [token]);

    const handleCreate = async () => {
        if (!token) return;
        await adminCreateGroup(token, newName, newSlug);
        setCreateOpen(false);
        setNewName('');
        setNewSlug('');
        load();
    };

    const openAssign = (group: any) => {
        setAssignGroup(group);
        setSelectedQuizIds(group.quizIds ?? []);
    };

    const handleAssign = async () => {
        if (!token || !assignGroup) return;
        await adminAssignQuizzes(token, assignGroup.id, selectedQuizIds);
        setAssignGroup(null);
        load();
    };

    const toggleQuiz = (id: number) =>
        setSelectedQuizIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Groups</Typography>
                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>New Group</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? <CircularProgress /> : (
                <Table size="small" sx={{ bgcolor: 'white', borderRadius: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Slug</TableCell>
                            <TableCell>Quizzes Assigned</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map(g => (
                            <TableRow key={g.id} hover>
                                <TableCell>{g.name}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{g.slug}</TableCell>
                                <TableCell>{(g.quizIds ?? []).length}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Assign quizzes">
                                        <IconButton size="small" onClick={() => openAssign(g)}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Create group dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>New Group</DialogTitle>
                <DialogContent>
                    <TextField label="Name" fullWidth sx={{ mt: 1, mb: 2 }} value={newName} onChange={e => setNewName(e.target.value)} />
                    <TextField label="Slug" fullWidth value={newSlug} onChange={e => setNewSlug(e.target.value)} helperText="e.g. triarc_general" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!newName || !newSlug}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Assign quizzes dialog */}
            <Dialog open={!!assignGroup} onClose={() => setAssignGroup(null)} fullWidth maxWidth="xs">
                <DialogTitle>Assign Quizzes — {assignGroup?.name}</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        {allQuizzes.map((q: any) => (
                            <FormControlLabel
                                key={q.id}
                                control={<Checkbox checked={selectedQuizIds.includes(Number(q.id))} onChange={() => toggleQuiz(Number(q.id))} />}
                                label={q.title ?? q.productName}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignGroup(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAssign}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
